import { FindingKind, ReportEvaluation, ReportFinding, ReportVerdict } from './types';

/**
 * One rule for turning findings into a verdict, so no report has to build
 * its own (or decide failure by matching finding ids).
 *
 * A finding says what kind of thing it found:
 *   defect   the product failed a check              → FAIL
 *   invalid  the record can't be relied on            → INVALID (verdict failed)
 *   gap      something is missing to close the report → INCOMPLETE
 *   note     worth saying; changes nothing
 *   pass     a check that passed, for the record
 *
 * Precedence: defect, then invalid, then gap, else pass.
 */

export interface VerdictLabels {
  pass?: string;
  defect?: string;
  invalid?: string;
  gap?: string;
}

const make = (kind: FindingKind, id: string, message: string): ReportFinding => ({
  id,
  kind,
  message,
  severity: kind === 'defect' || kind === 'invalid' ? 'fail' : kind === 'gap' ? 'warn' : 'info',
  passed: kind === 'pass',
});

export const finding = {
  defect: (id: string, message: string) => make('defect', id, message),
  invalid: (id: string, message: string) => make('invalid', id, message),
  gap: (id: string, message: string) => make('gap', id, message),
  note: (id: string, message: string) => make('note', id, message),
  pass: (id: string, message: string) => make('pass', id, message),
  /** A check: a pass when `ok`, otherwise a finding of `kind` (a defect unless said). */
  check: (id: string, ok: boolean, passMessage: string, failMessage: string, kind: Exclude<FindingKind, 'pass' | 'note'> = 'defect') => (ok ? make('pass', id, passMessage) : make(kind, id, failMessage)),
};

/** A finding's kind; findings written before kinds existed are read from their severity. */
export function kindOf(f: ReportFinding): FindingKind {
  if (f.kind) return f.kind;
  if (f.passed) return 'pass';
  return f.severity === 'fail' ? 'defect' : f.severity === 'warn' ? 'gap' : 'note';
}

/** Findings that stand in the way of a pass: what the header lists as open. */
export function isOpen(f: ReportFinding): boolean {
  const kind = kindOf(f);
  return kind === 'defect' || kind === 'invalid' || kind === 'gap';
}

export function verdictFrom(findings: ReportFinding[], labels: VerdictLabels = {}): ReportEvaluation {
  const kinds = new Set(findings.map(kindOf));
  const [verdict, verdictLabel]: [ReportVerdict, string] = kinds.has('defect')
    ? ['failed', labels.defect ?? 'FAIL']
    : kinds.has('invalid')
      ? ['failed', labels.invalid ?? 'INVALID']
      : kinds.has('gap')
        ? ['incomplete', labels.gap ?? 'INCOMPLETE']
        : ['passed', labels.pass ?? 'PASS'];
  return { verdict, verdictLabel, findings };
}
